import { useEffect, useState } from "react";

import { TimeScaleOptions } from "chart.js";
import { TimeUnit } from "chart.js/types/adapters";
import moment, { Moment } from "moment";

import { ResolutionType } from "../store/common_types";

export const rangeResolutionLimit = (rangeDiffDays: number, resolution: ResolutionType): ResolutionType => {
  if (rangeDiffDays > 31 * 6) return "1m";
  if (rangeDiffDays > 31) return ["1m", "1d"].includes(resolution) ? resolution : "1d";
  if (rangeDiffDays > 7) return ["1m", "1d", "1h"].includes(resolution) ? resolution : "1h";
  return resolution;
};

export const resolutionUnitMap: { [K in ResolutionType]: Partial<TimeScaleOptions["time"]> } = {
  "10min": { tooltipFormat: "YYYY-MM-DD HH:mm", stepSize: 10, minUnit: "minute" as TimeUnit },
  "1h": { tooltipFormat: "YYYY-MM-DD HH:mm", stepSize: 1, minUnit: "hour" as TimeUnit, unit: "hour" as TimeUnit },
  "1d": { tooltipFormat: "YYYY-MM-DD", stepSize: 1, minUnit: "day" as TimeUnit, unit: "day" as TimeUnit },
  "1m": { tooltipFormat: "YYYY-MM", stepSize: 1, minUnit: "month" as TimeUnit, unit: "month" as TimeUnit },
};

export const minRangeWindowMap: { [K in ResolutionType]: number } = {
  "10min": 1000 * 60 * 60 * 4, // 4 hours (14 400 000 ms)
  "1h": 1000 * 60 * 60 * 24, // 24 hours (86 400 000 ms)
  "1d": 1000 * 60 * 60 * 24 * 24, // 24 days (2 073 600 000 ms)
  "1m": 1000 * 60 * 60 * 24 * 30 * 24, // 24 months (62 208 000 000 ms)
};

const maxLimitMap = {
  "10min": (to: Moment) => {
    if (to >= moment().utc().startOf("day")) {
      return moment()
        .utc()
        .subtract(to.minutes() % 10, "minute")
        .valueOf();
    }
    return to.startOf("day").add(1, "day").subtract(10, "minute").valueOf();
  },
  "1h": (to: Moment) => {
    if (to >= moment().utc().startOf("day")) {
      return moment().utc().startOf("hour").valueOf();
    }
    return to.startOf("day").add(1, "day").subtract(1, "hour").valueOf();
  },
  "1d": (to: Moment) => {
    if (to >= moment().utc().startOf("day")) {
      return moment().utc().startOf("day").valueOf();
    }
    return to.startOf("day").valueOf();
  },
  "1m": (to: Moment) => to.startOf("month").valueOf(),
};

export const useRangeLimit = (rangeFrom: Moment, rangeTo: Moment, resolution: ResolutionType) => {
  const [limit, setLimit] = useState({ min: 0, max: 1 });

  useEffect(() => {
    const min = moment(rangeFrom).utcOffset("Z", true).startOf("day").valueOf();
    const max = maxLimitMap[resolution](moment(rangeTo).utcOffset("Z", true));

    setLimit((state) => {
      if (min !== state.min || max !== state.max) {
        return { min, max };
      }
      return state;
    });
  }, [rangeFrom, rangeTo, resolution]);

  return limit;
};
